import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import { SvgIcon } from 'components/helpers/SvgIcon';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { PreambleOrientation, Preamble } from 'components/helpers/Molecules/Preamble/Preamble';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { HeadlineFields, CtaFields } from 'lib/types/headlineFields';
import { Themes } from 'lib/themes';
import carouselStyles from './Carousel.module.scss';
import cardListingStyles from '../CardListing.module.scss';
import clsx from 'clsx';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useCtaComponentClass } from 'lib/hooks/theming/useCtaComponentClass';
import useDictionary from 'lib/hooks/useDictionary';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';

// Cap the rAF retry loop that waits for Splide to initialize. Without a cap, if
// Splide never mounts (empty/hidden in the editor preview), the loop reschedules
// every frame forever and freezes the tab.
const MAX_SPLIDE_BIND_ATTEMPTS = 120;

// The live carousel displays at most 8 cards (matches the dynamic Carousel). In the
// editor we still render every authored card so they remain manageable, plus a warning.
const MAX_CARDS = 8;

interface CarouselStaticTrackProps {
  /**
   * Authored card components dropped into the rendering's placeholder.
   * Unlike the dynamic Carousel (which derives slides from a search result),
   * the static variant renders whatever the author places in the placeholder.
   */
  slides: ReactNode[];
  preambleAlignment?: PreambleOrientation;
  /**
   * When true (Experience Editor / Pages), render the slides as a plain stacked
   * list so authors can add/remove/edit cards. Splide reorders/clones DOM, which
   * breaks the editing chrome and drag-and-drop.
   */
  isEditing?: boolean;
}

/**
 * The carousel track (Splide + controls). Kept separate from the Preamble wrapper
 * so the Preamble is rendered once, outside the placeholder render callback —
 * otherwise the editing chrome duplicates the headline/preamble markup.
 */
export const CarouselStaticTrack = ({
  slides,
  preambleAlignment,
  isEditing,
}: CarouselStaticTrackProps) => {
  const carouselPaginationColor = useCtaComponentClass('component-carousel-pagination-color');
  const { getDictionaryValue } = useDictionary();

  const splideRef = useRef<Splide>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [currentPerPage, setCurrentPerPage] = useState(preambleAlignment === 'SideLeft' ? 3 : 4);

  // Live carousel is capped to MAX_CARDS; the editor keeps all cards visible.
  const visibleSlides = isEditing ? slides : slides.slice(0, MAX_CARDS);
  const totalCards = visibleSlides.length;

  const slideFocusRefs = useRef<Array<HTMLDivElement | null>>([]);
  const pendingFocusDirectionRef = useRef<'prev' | 'next' | null>(null);
  const pendingFocusKeyboardRef = useRef<boolean>(false);
  const pendingTabFocusIndexRef = useRef<number | null>(null);
  const pendingMoveFocusRef = useRef<boolean>(false);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  const lastLiveMessageRef = useRef<string>('');
  const announceItemRef = useRef<boolean>(false);
  const pendingAnnounceDirectionRef = useRef<'prev' | 'next' | null>(null);

  const announce = (message: string) => {
    if (typeof window === 'undefined') return;

    const liveRegion = liveRegionRef.current;
    if (!liveRegion) return;
    if (!message || message === lastLiveMessageRef.current) return;

    lastLiveMessageRef.current = message;
    liveRegion.textContent = '';
    window.requestAnimationFrame(() => {
      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = message;
      }
    });
  };

  const focusSlide = (indexToFocus: number) => {
    const container = slideFocusRefs.current[indexToFocus];
    if (!container) return;

    container.focus();
  };

  // Track Splide position so arrows/pagination reflect the current state.
  useEffect(() => {
    if (isEditing) return;
    if (!splideRef.current?.splide) return;
    const splide = splideRef.current.splide;

    const updateStates = () => {
      const perPage = Number(splide.options.perPage) || 1;
      setActiveIndex(splide.index);
      setIsAtStart(splide.index === 0);
      setIsAtEnd(splide.index >= totalCards - perPage);
      setCurrentPerPage(perPage);
    };

    splide.on('move', updateStates);
    splide.on('resize', updateStates);
    updateStates();

    return () => {
      splide.off('move');
      splide.off('resize');
    };
  }, [totalCards, isEditing]);

  // Move keyboard/click focus to the newly revealed card after a slide change.
  useEffect(() => {
    // No Splide instance is mounted in the editor, so skip the rAF bind loop
    // entirely — otherwise it reschedules every frame forever and freezes Pages.
    if (isEditing) return;
    if (typeof window === 'undefined') return;

    let rafId = 0;
    let attempts = 0;
    let isBound = false;

    const onMoved = () => {
      const direction = pendingFocusDirectionRef.current;
      pendingFocusDirectionRef.current = null;
      const shouldTabFocus = pendingFocusKeyboardRef.current;
      pendingFocusKeyboardRef.current = false;

      const splide = splideRef.current?.splide;
      if (!splide) return;

      const perPage = Number(splide.options.perPage) || 1;
      const startIndex = splide.index;
      const indexToFocus =
        direction === 'next'
          ? Math.min(totalCards - 1, startIndex + perPage - 1)
          : Math.max(0, startIndex);

      if (pendingMoveFocusRef.current) {
        pendingMoveFocusRef.current = false;
        window.requestAnimationFrame(() => focusSlide(indexToFocus));
      }

      if (shouldTabFocus) {
        pendingTabFocusIndexRef.current = indexToFocus;
      }
    };

    const tryBind = () => {
      const splide = splideRef.current?.splide;
      if (!splide) {
        if (attempts++ < MAX_SPLIDE_BIND_ATTEMPTS) {
          rafId = window.requestAnimationFrame(tryBind);
        }
        return;
      }

      splide.on('moved.carouselFocus', onMoved);
      isBound = true;
    };

    tryBind();

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      const splide = splideRef.current?.splide;
      if (isBound && splide) {
        splide.off('moved.carouselFocus');
      }
    };
  }, [totalCards, isEditing]);

  // Announce the current position generically. Static slides are opaque authored
  // components, so there is no per-card Title to read (unlike the dynamic Carousel).
  useEffect(() => {
    // No Splide instance is mounted in the editor, so skip the rAF bind loop
    // entirely — otherwise it reschedules every frame forever and freezes Pages.
    if (isEditing) return;
    if (typeof window === 'undefined') return;

    let rafId = 0;
    let attempts = 0;
    let isBound = false;

    const onMovedLive = () => {
      if (!announceItemRef.current) return;

      const direction = pendingAnnounceDirectionRef.current;
      announceItemRef.current = false;
      pendingAnnounceDirectionRef.current = null;
      if (!direction) return;

      const splide = splideRef.current?.splide;
      if (!splide) return;
      if (totalCards <= 0) return;

      const perPage = Number(splide.options.perPage) || 1;
      const startIndex = splide.index;
      const indexToAnnounce =
        direction === 'next'
          ? Math.min(totalCards - 1, startIndex + perPage - 1)
          : Math.max(0, startIndex);

      announce(`Item ${indexToAnnounce + 1} of ${totalCards}`);
    };

    const tryBind = () => {
      const splide = splideRef.current?.splide;
      if (!splide) {
        if (attempts++ < MAX_SPLIDE_BIND_ATTEMPTS) {
          rafId = window.requestAnimationFrame(tryBind);
        }
        return;
      }

      splide.on('moved.carouselLive', onMovedLive);
      isBound = true;
    };

    tryBind();

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      const splide = splideRef.current?.splide;
      if (isBound && splide) {
        splide.off('moved.carouselLive');
      }
    };
  }, [totalCards, isEditing]);

  const handlePrev = () => {
    splideRef.current?.splide?.go('<');
  };

  const handleNext = () => {
    splideRef.current?.splide?.go('>');
  };

  const onArrowKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    if (pendingTabFocusIndexRef.current === null) return;

    const indexToFocus = pendingTabFocusIndexRef.current;
    pendingTabFocusIndexRef.current = null;

    e.preventDefault();
    window.requestAnimationFrame(() => focusSlide(indexToFocus));
  };

  const onPrev = (e: React.MouseEvent<HTMLButtonElement>) => {
    announceItemRef.current = true;
    pendingAnnounceDirectionRef.current = 'prev';
    pendingMoveFocusRef.current = true;
    pendingFocusDirectionRef.current = 'prev';
    pendingFocusKeyboardRef.current = e.detail === 0;
    handlePrev();
  };

  const onNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    announceItemRef.current = true;
    pendingAnnounceDirectionRef.current = 'next';
    pendingMoveFocusRef.current = true;
    pendingFocusDirectionRef.current = 'next';
    pendingFocusKeyboardRef.current = e.detail === 0;
    handleNext();
  };

  // In the editor, skip Splide (it clones/reorders DOM and breaks the editing
  // chrome + drag-and-drop) and instead preview the cards in the shared content-grid
  // layout. This mirrors MixedMediaGrid so the editor shows columns (1 mobile /
  // 2 tablet / 4 desktop) instead of one tall stack. The live carousel shows 4 per
  // view (3 for SideLeft), so we match the column count to that.
  if (isEditing) {
    const editorColCount = preambleAlignment === 'SideLeft' ? 3 : 4;
    return (
      <>
        {slides.length > MAX_CARDS && (
          <EditingHelpText priority="warning">
            {`Only the first ${MAX_CARDS} cards display on the published carousel.`}
          </EditingHelpText>
        )}
        <ul
          className={clsx(
            cardListingStyles.contentGridContent,
            cardListingStyles[`col-${editorColCount}`]
          )}
        >
          {slides.map((slide, index) => (
            <li
              key={index}
              className={clsx(
                cardListingStyles.contentGridCard,
                cardListingStyles[`col-${editorColCount}`]
              )}
            >
              {slide}
            </li>
          ))}
        </ul>
      </>
    );
  }

  // Pages = number of valid start positions for the current perPage.
  const totalPages = Math.max(1, totalCards - currentPerPage + 1);

  return (
    <div
      className={clsx(carouselStyles.carouselContainer, {
        [carouselStyles.carouselContainerForSideLeft]: preambleAlignment === 'SideLeft',
      })}
    >
      <div
        ref={liveRegionRef}
        className="screenReaderText"
        aria-live="polite"
        aria-atomic="true"
        role="status"
      />
      <div className={clsx(carouselStyles.carouselWithArrows)}>
        <CtaButton
          onClick={onPrev}
          onKeyDown={onArrowKeyDown}
          ctaStyle="none"
          className={clsx(carouselStyles.carouselPrev, carouselStyles[carouselPaginationColor], {
            [carouselStyles.carouselArrowHidden]: isAtStart,
          })}
          aria-label={getDictionaryValue('Components.Carousel.LeftArrow')}
        >
          <SvgIcon icon="chevron-left" size="sm" viewBox="0 0 14 22" />
        </CtaButton>

        <Splide
          ref={splideRef}
          options={{
            perPage: preambleAlignment === 'SideLeft' ? 3 : 4,
            perMove: 1,
            gap: '1rem',
            pagination: false,
            arrows: false,
            speed: 300,
            rewind: false,
            drag: true,
            breakpoints: {
              767: {
                perPage: 1,
              },
              1199: {
                perPage: preambleAlignment === 'SideLeft' ? 2 : 3,
              },
              1440: {
                perPage: preambleAlignment === 'SideLeft' ? 2 : 3,
              },
            },
            live: false,
          }}
          className={carouselStyles.carouselDiv}
        >
          {visibleSlides.map((slide, index) => (
            <SplideSlide key={index}>
              <div
                ref={(el) => {
                  slideFocusRefs.current[index] = el;
                }}
                tabIndex={-1}
                className={carouselStyles.focusWrapper}
              >
                {slide}
              </div>
            </SplideSlide>
          ))}
        </Splide>

        <CtaButton
          onClick={onNext}
          onKeyDown={onArrowKeyDown}
          ctaStyle="none"
          className={clsx(carouselStyles.carouselNext, carouselStyles[carouselPaginationColor], {
            [carouselStyles.carouselArrowHidden]: isAtEnd,
          })}
          aria-label={getDictionaryValue('Components.Carousel.RightArrow')}
        >
          <SvgIcon icon="chevron-right" size="sm" viewBox="0 0 14 22" />
        </CtaButton>
      </div>

      {/* Desktop pagination - below the carousel */}
      <div className={carouselStyles.customPaginationDesktop}>
        {Array.from({ length: totalPages }).map((_, index) => (
          <span
            key={index}
            className={clsx(
              carouselStyles.paginationDot,
              carouselStyles[carouselPaginationColor],
              activeIndex === index && carouselStyles.paginationDotActive,
              carouselStyles[carouselPaginationColor]
            )}
          />
        ))}
      </div>

      {/* Mobile controls with pagination */}
      <div className={carouselStyles.carouselControlsMobile}>
        <CtaButton
          onClick={onPrev}
          onKeyDown={onArrowKeyDown}
          ctaStyle="none"
          ctaVariant="brand"
          className={clsx(
            carouselStyles.carouselPrevMobile,
            carouselStyles[carouselPaginationColor],
            {
              [carouselStyles.carouselArrowHidden]: isAtStart,
            }
          )}
          aria-label={getDictionaryValue('Components.Carousel.LeftArrow')}
        >
          <SvgIcon icon="chevron-left" size="sm" viewBox="0 0 14 22" />
        </CtaButton>

        <div className={carouselStyles.customPaginationMobile}>
          {Array.from({ length: totalPages }).map((_, index) => (
            <span
              key={index}
              className={clsx(
                carouselStyles.paginationDot,
                carouselStyles[carouselPaginationColor],
                activeIndex === index && carouselStyles.paginationDotActive,
                carouselStyles[carouselPaginationColor]
              )}
            />
          ))}
        </div>

        <CtaButton
          onClick={onNext}
          onKeyDown={onArrowKeyDown}
          ctaStyle="none"
          ctaVariant="brand"
          className={clsx(
            carouselStyles.carouselNextMobile,
            carouselStyles[carouselPaginationColor],
            {
              [carouselStyles.carouselArrowHidden]: isAtEnd,
            }
          )}
          aria-label={getDictionaryValue('Components.Carousel.RightArrow')}
        >
          <SvgIcon icon="chevron-right" size="sm" viewBox="0 0 14 22" />
        </CtaButton>
      </div>
    </div>
  );
};

export interface CarouselStaticProps extends HeadlineFields, CtaFields {
  theme?: Themes;
  preambleOrientation?: PreambleOrientation;
  /** The carousel track (placeholder-driven). Rendered as the Preamble's children. */
  content?: ReactNode;
}

export const CarouselStatic = (props: CarouselStaticProps) => {
  return (
    <BrandAndThemeProvider
      theme={props.theme}
      className={clsx(carouselStyles.carouselWrapper)}
      data-component="authorable/shared/card-listing/carousel/carousel-static"
    >
      <Preamble
        headlineEyebrow={props.headlineEyebrow}
        headlineIcon={props.headlineIcon}
        headline={props.headline}
        headlineSubhead={props.headlineSubhead}
        headlineDescription={props.headlineDescription}
        cta1={props.cta1}
        cta2={props.cta2}
        align={props.preambleOrientation}
      >
        {props.content}
      </Preamble>
    </BrandAndThemeProvider>
  );
};
